import { useAuthActions } from '@convex-dev/auth/react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { api } from '@repo/backend';
import { useQuery } from 'convex/react';
import { useEffect, useState } from 'react';
import { normalizeRedirectTarget } from '@/lib/auth';
import { toUserFriendlyErrorMessage } from '@/lib/errors';

export const Route = createFileRoute('/register')({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => {
    const redirect = normalizeRedirectTarget(search.redirect);
    return redirect ? { redirect } : {};
  },
  component: RegisterPage,
});

function RegisterPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const { signIn } = useAuthActions();
  const currentUser = useQuery(api.users.queries.me);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const redirectTarget = redirect ?? '/';

  useEffect(() => {
    if (currentUser) {
      void navigate({ to: redirectTarget });
    }
  }, [currentUser, navigate, redirectTarget]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const result = await signIn('password', {
        flow: 'signUp',
        name: username.trim(),
        email: email.trim().toLowerCase(),
        password,
        redirectTo: redirectTarget,
      });

      if (result.redirect) {
        window.location.assign(result.redirect.toString());
        return;
      }

      await navigate({ to: redirectTarget });
    } catch (error) {
      setErrorMessage(
        toUserFriendlyErrorMessage(
          error,
          'Unable to create your account right now. Please try again.',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-8 bg-background relative overflow-hidden">
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-secondary/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        <div className="border border-iron-800 bg-iron-900/80 backdrop-blur-sm p-8 shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-[2px] bg-iron-800 group-hover:bg-primary/50 transition-colors" />
          <div className="absolute top-0 right-0 w-[2px] h-full bg-iron-800 group-hover:bg-gradient-to-b group-hover:from-primary/50 group-hover:to-transparent transition-all" />

          <div className="text-center mb-8">
            <h1 className="font-epic text-3xl tracking-[0.1em] text-white uppercase mb-2">
              Register
            </h1>
            <p className="text-iron-400 text-xs tracking-widest uppercase mb-4">
              Pledge Your Fealty
            </p>
            <div className="w-12 h-1 bg-primary mx-auto shadow-[0_0_8px_rgba(163,41,39,0.6)]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label
                htmlFor="username"
                className="block text-[10px] tracking-[0.2em] uppercase font-bold text-iron-300"
              >
                House Name (Username)
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full h-12 bg-iron-950 border border-iron-800 px-4 text-iron-100 placeholder:text-iron-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all font-sans"
                placeholder="House Stark"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block text-[10px] tracking-[0.2em] uppercase font-bold text-iron-300"
              >
                Ravens Address (Email)
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full h-12 bg-iron-950 border border-iron-800 px-4 text-iron-100 placeholder:text-iron-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all font-sans"
                placeholder="lord@winterfell.com"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="password"
                className="block text-[10px] tracking-[0.2em] uppercase font-bold text-iron-300"
              >
                Secret Seal (Password)
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full h-12 bg-iron-950 border border-iron-800 px-4 text-iron-100 placeholder:text-iron-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all font-sans"
                placeholder="••••••••"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="confirmPassword"
                className="block text-[10px] tracking-[0.2em] uppercase font-bold text-iron-300"
              >
                Confirm Seal
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full h-12 bg-iron-950 border border-iron-800 px-4 text-iron-100 placeholder:text-iron-600 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/50 transition-all font-sans"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-12 bg-primary text-primary-foreground font-bold tracking-[0.2em] uppercase text-xs hover:bg-primary/90 hover:shadow-[0_0_15px_rgba(163,41,39,0.4)] disabled:cursor-not-allowed disabled:opacity-70 transition-all cursor-pointer relative overflow-hidden group"
            >
              <span className="relative z-10">
                {isSubmitting ? 'Forging Alliance' : 'Forge Alliance'}
              </span>
              <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
            </button>

            {errorMessage ? (
              <div className="border border-primary/40 bg-primary/10 px-4 py-3 text-xs text-primary">
                {errorMessage}
              </div>
            ) : (
              <p className="text-[11px] text-iron-500">
                Passwords must be at least 8 characters long.
              </p>
            )}
          </form>

          <div className="mt-8 text-center border-t border-iron-800 pt-6">
            <p className="text-xs text-iron-400">
              Already sworn fealty?{' '}
              <Link
                to="/sign-in"
                search={redirect ? { redirect } : {}}
                className="text-primary hover:text-white transition-colors uppercase tracking-wider font-bold ml-1"
              >
                Enter Here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
